import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, ShieldCheck } from "lucide-react";

/** Manueller Recovery-Export. Read-only: es werden ausschließlich Daten gelesen. */
export const BackupTab = () => {
  const { t, fmtDate } = useI18n();
  const { user } = useAuth();
  const isPlatform = ["superadmin", "dev"].includes(user?.platform_role);
  const [scope, setScope] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);

  const runImport = async (mode) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/backup/import", fd, { params: { mode } });
      setReport(res.data);
      if (mode === "restore_missing") { toast.success(t("bk.inserted") + ": " + res.data.inserted_total); load(); }
      else if (res.data.verdict !== "PASS") toast.error(res.data.errors?.[0] || res.data.verdict);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/backup/scope", { params: { scope: "tenant" } }),
        api.get("/backup/history"),
      ]);
      setScope(s.data); setHistory(h.data.rows || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const download = async (mode) => {
    if (mode === "platform" && !window.confirm(t("bk.confirmPlatform"))) return;
    setBusy(true);
    try {
      const res = await api.post("/backup/export", null, {
        params: { scope: mode }, responseType: "blob",
      });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1]
        || `clubops_backup_${mode}.zip`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success(t("bk.done"));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="backup-tab">
      <div className="rounded-md border border-border bg-muted/30 p-4 text-sm">
        <div className="flex items-center gap-2 font-medium"><ShieldCheck className="h-4 w-4" />{t("bk.title")}</div>
        <p className="mt-2 text-xs text-muted-foreground">{t("bk.hint")}</p>
      </div>

      {scope && (
        <div className="text-sm" data-testid="backup-scope">
          <div>{t("bk.records")}: <span className="font-mono font-semibold">{scope.total_records}</span></div>
          <div className="mt-1 text-xs text-muted-foreground">
            {t("bk.excluded")}: {scope.excluded_collections.join(", ")}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => download("tenant")} disabled={busy} data-testid="backup-tenant-btn">
          <Download className="mr-1.5 h-4 w-4" />{t("bk.tenantBackup")}
        </Button>
        {isPlatform && (
          <Button variant="outline" onClick={() => download("platform")} disabled={busy}
            data-testid="backup-platform-btn">
            <Download className="mr-1.5 h-4 w-4" />{t("bk.platformBackup")}
          </Button>)}
      </div>

      <div className="space-y-3 rounded-md border border-border p-4" data-testid="backup-import">
        <div className="text-sm font-medium">{t("bk.import")}</div>
        <p className="text-xs text-muted-foreground">{t("bk.importHint")}</p>
        <input type="file" accept=".zip" data-testid="backup-import-file"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
          className="block w-full max-w-md text-xs" />
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={!file || busy}
            onClick={() => runImport("validate")} data-testid="backup-import-check">
            {t("bk.check")}
          </Button>
          <Button size="sm" disabled={!file || busy || report?.verdict !== "PASS"}
            onClick={() => runImport("restore_missing")} data-testid="backup-import-apply">
            {t("bk.apply")}
          </Button>
        </div>
        {report && (
          <div className="space-y-1 text-xs" data-testid="backup-import-report">
            <div>
              {report.verdict} · {t("bk.scope")}: {report.scope}
              {" · "}{t("bk.missing")}: <span className="font-mono">{report.missing_total}</span>
              {" · "}{t("bk.identical")}: <span className="font-mono">{report.identical_total}</span>
              {" · "}{t("bk.conflicting")}: <span className="font-mono font-semibold">{report.conflicting_total}</span>
              {" · "}{t("bk.skipped")}: <span className="font-mono">{report.skipped_total}</span>
              {" · "}{t("bk.inserted")}: <span className="font-mono">{report.inserted_total}</span>
            </div>
            {(report.conflicts || []).slice(0, 10).map((c) => (
              <div key={`${c.collection}-${c.id}`} className="text-amber-600 dark:text-amber-500"
                data-testid="backup-import-conflict">
                {c.collection} · {c.id}: {(c.fields || [c.field]).join(", ")}
              </div>))}
            {(report.warnings || []).map((w) => (
              <div key={w} className="text-amber-600 dark:text-amber-500">{w}</div>))}
            {(report.errors || []).map((e2) => (
              <div key={e2} className="text-destructive">{e2}</div>))}
          </div>)}
      </div>

      {history.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs" data-testid="backup-history">
            <thead className="bg-muted/50"><tr>
              <th className="p-2 text-left">{t("common.date")}</th>
              <th className="p-2 text-left">{t("bk.scope")}</th>
              <th className="p-2 text-right">{t("bk.records")}</th>
              <th className="p-2 text-right">{t("bk.size")}</th>
              <th className="p-2 text-left">{t("bk.reconciliation")}</th>
            </tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-border">
                  <td className="p-2">{fmtDate(h.created_at)}</td>
                  <td className="p-2">{h.scope}</td>
                  <td className="p-2 text-right font-mono">{h.total_records}</td>
                  <td className="p-2 text-right font-mono">{Math.round((h.size_bytes || 0) / 1024)} KB</td>
                  <td className="p-2">{h.reconciliation}</td>
                </tr>))}
            </tbody>
          </table>
        </div>)}
    </div>
  );
};

export default BackupTab;
